import React, { Component } from 'react'
import i18n from 'live/services/i18n'
import moment from 'moment'
import DateService from 'live/services/date'
import Utils from 'live/lib/utils'

import { HelpBlock } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import MaskedInput from 'react-maskedinput'
import CustomDateInput from 'live/components/forms/ui/custom-react-datepicker-input'
import SaveButton from 'live/components/forms/ui/button-save'
import FormGroup from 'live/components/forms/ui/form-group'
import xhr from 'live/services/xhr'
import _ from 'lodash'

import { ANNOTATIONS_URL } from '../timeline/components/timeline'

const formColors = [
    '#37a3ef',
    'rgb(1, 194, 146)', // success
    '#F49703', // warning
    'rgb(196, 53, 53)' // danger
]

type Props = {
    dashboardId: number
    event: AnnotationEvent
    mode: 'temporal' | 'numberic'
    onHide: Function
    liftSubmitFn?: Function
    activeDashboardFilters: {
        [filter: string]: Array<string>
    }
}

type State = {
    submitted: boolean
    form: Object
    tooltip: {
        value: typeof moment
    }
}

export default class AnnotationDefaultForm extends Component<Props, State> {
    static defaultProps = {
        mode: 'temporal',
        submitted: false
    }

    constructor(props: Props) {
        super(props)
        this.props = props

        const event = props.event || { begin: new Date() }
        const isTemporal = props.mode === 'temporal'

        this.state = {
            submitted: false,
            tooltip: {
                value: isTemporal ? DateService.getMoment(event.begin) : event.begin
            },
            form: {
                ...getFormInitialState(),
                ...this.extractStateFromEvent(event, isTemporal)
            }
        }
    }

    extractStateFromEvent = event => ({
        color: event.__color || formColors[0],
        message: event.message || '',
        fromDate: event.begin ? moment(event.begin) : null,
        fromTime: event.begin ? moment(event.begin).format('HH:mm:ss') : null,
        toDate: event.end ? moment(event.end) : null,
        toTime: event.end ? moment(event.end).format('HH:mm:ss') : null
    })

    getNormalizedInputData = () => {
        const { tooltip, form } = this.state
        const { event, mode } = this.props
        const isTemporal = mode === 'temporal'
        const fromDate = form.fromDate || (isTemporal && tooltip.value)
        const fromTime = form.fromTime || (isTemporal ? tooltip.value.format('HH:mm:ss') : undefined)
        const queryParams = Utils.getQueryParams()

        let begin
        let end

        if (isTemporal) {
            begin = getUnixFromDateAndTime(fromDate, fromTime)
            end = moment.isMoment(form.toDate) && getUnixFromDateAndTime(form.toDate, form.toTime)
        } else {
            begin = parseInt(form.fromNumeric !== undefined ? form.fromNumeric : tooltip.value)
            end = parseInt(form.toNumeric)
        }

        return {
            uid: (event && event.uid) || undefined,
            begin,
            end: isNaN(end) || end ? end : null,
            dashboardSpan: (event && event.dashboardSpan) || (queryParams ? queryParams.span : undefined),
            __color: form.color,
            message: form.message,
            __src: 'dashboard',
            room: event.room,
            dashboardId: this.props.dashboardId,
            dashboardFilters: this.props.activeDashboardFilters
        }
    }

    submit = () => {
        return new Promise((resolve, reject) => {
            const isTemporal = this.props.mode === 'temporal'
            const data = this.getNormalizedInputData()
            const { form, submitted } = this.state
            const editing = !!data.uid

            if (!submitted) {
                this.setState({
                    submitted: true
                })
            }

            if (!this.validate(isTemporal, data, form)) return reject(new Error('Invalid input'))

            xhr.post(ANNOTATIONS_URL, data)
                .then(({ data }) => {
                    resolve({
                        data,
                        form,
                        initialState: getFormInitialState()
                    })

                    this.props.onHide(data, form, getFormInitialState(), editing)
                })
                .catch(e => console.error(e) || reject(e))
        })
    }

    /**
     * Validates data before reaching the API.
     *
     * @param {bool} isTemporal defines if the dashboard is in "temporal" mode
     * @param {object} {begin, end, message} data object destructured
     * @param end
     * @param message
     * @param {object} {toDate, toTime = '', fromTime = '', fromDate, toNumeric} form object destructured
     *
     * @param toTime
     * @param fromTime
     * @param fromDate
     * @param toNumeric
     * @returns {bool} Whether the data is valid or not.
     *
     * @memberof Timeline
     */
    validate = (
        isTemporal: boolean,
        { begin, end, message },
        { toDate, toTime = '', fromTime = '', fromDate = this.state.tooltip.value, toNumeric }
    ) => {
        const errors = new Map()

        if (!message.length) errors.set('message-empty', true)

        if (isTemporal) {
            if (end && begin > end) errors.set('begin-greater-end', true)

            if ((toDate && toTime && !toTime.length) || (!toDate && toTime && toTime.length)) {
                errors.set('invalid-end', true)
            }

            if (isNaN(begin)) errors.set('invalid-from', true)

            if (isNaN(end)) errors.set('invalid-end', true)
        } else {
            if (toNumeric && toNumeric.length && begin > end) errors.set('begin-greater-end', true)
        }

        this.setFormValue('errors', errors)

        return !errors.size
    }

    onChangeFromDate = (mom: moment) => {
        this.setFormValue('fromDate', mom, () =>
            this.validate(this.props.mode === 'temporal', this.getNormalizedInputData(), this.state.form)
        )
        setTimeout(() => this.refs.fromTime.focus())
    }

    onChangeFromTime = (e: Object) => {
        this.setFormValue('fromTime', e.target.value, () =>
            this.validate(this.props.mode === 'temporal', this.getNormalizedInputData(), this.state.form)
        )
    }

    onChangeToDate = (mom: moment) => {
        this.setFormValue('toDate', mom, () =>
            this.validate(this.props.mode === 'temporal', this.getNormalizedInputData(), this.state.form)
        )
        setTimeout(() => this.refs.toTime.focus())
    }

    onChangeToTime = (e: Object) => {
        this.setFormValue('toTime', e.target.value, () =>
            this.validate(this.props.mode === 'temporal', this.getNormalizedInputData(), this.state.form)
        )
    }

    setDateRaw = (formProp: string, value: string) => {
        value = value.replace(/[^0-9.]/g, '')
        if (value.length < 8) {
            this.setFormValue(formProp, undefined)

            return
        }

        const m = DateService.getMoment(value, DateService.getDateFormat())

        this.setFormValue(formProp, m)
    }

    setFormValue = (key: string, value: any, cb?: Function) => {
        this.setState(
            {
                form: {
                    ...this.state.form,
                    [key]: value
                }
            },
            cb
        )
    }

    componentDidMount() {
        if (_.isFunction(this.props.liftSubmitFn)) this.props.liftSubmitFn(this.submit)
    }

    render() {
        const { form, tooltip, submitted } = this.state
        const { mode, showActions } = this.props

        const errors = {
            toDate: submitted && (form.errors.get('begin-greater-end') || form.errors.get('invalid-end')),
            fromDate: submitted && form.errors.get('invalid-from')
        }

        return (
            <div className="timeline__form">
                <ul className="timeline__color-picker">
                    {[0, 1, 2, 3].map((e, i) => (
                        <li
                            key={i}
                            onClick={() => this.setFormValue('color', formColors[i])}
                            className={`timeline__picker_${i} ${form.color === formColors[i] ? 'selected' : ''}`}
                        />
                    ))}
                </ul>

                <div className="timeline_date-picker">
                    <label htmlFor="timeline-from-date-picker">{i18n('From')}:</label>
                    {mode === 'temporal' ? (
                        <div className="flex flex-space-between">
                            <div className="flex-2 mr-5">
                                <DatePicker
                                    id="timeline-from-date-picker"
                                    customInput={<CustomDateInput invalid={errors.fromDate} />}
                                    autoComplete="false"
                                    selected={form.fromDate !== undefined ? form.fromDate : tooltip.value}
                                    onChange={this.onChangeFromDate}
                                    maxDate={moment()}
                                    dateFormat={DateService.getDateFormat()}
                                    placeholderText={DateService.getDateFormat().toLowerCase()}
                                    fixedHeight
                                    peekNextMonth
                                    showYearDropdown
                                    showMonthDropdown
                                />
                            </div>
                            <div className="flex-1 ml-5">
                                <MaskedInput
                                    className={errors.fromDate ? 'error' : ''}
                                    type="text"
                                    ref="fromTime"
                                    value={
                                        form.fromTime !== undefined ? form.fromTime : tooltip.value.format('HH:mm:ss')
                                    }
                                    onChange={this.onChangeFromTime}
                                    mask="11:11:11"
                                />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <FormGroup
                                type="text"
                                value={form.fromNumeric !== undefined ? form.fromNumeric : tooltip.value}
                                onChange={e =>
                                    this.setFormValue('fromNumeric', e.target.value, () =>
                                        this.validate(
                                            this.props.mode === 'temporal',
                                            this.getNormalizedInputData(),
                                            this.state.form
                                        )
                                    )
                                }
                            />
                        </div>
                    )}
                </div>
                <div />
                <label htmlFor="timeline-to-date-picker">
                    {i18n('To')} ({i18n('optional')}):
                </label>
                {mode === 'temporal' ? (
                    <div className="flex flex-space-between">
                        <div className="flex-2 mr-5">
                            <DatePicker
                                id="timeline-to-date-picker"
                                customInput={<CustomDateInput invalid={errors.toDate} />}
                                autoComplete="false"
                                selected={form.toDate}
                                onChange={this.onChangeToDate}
                                openToDate={form.fromDate}
                                minDate={form.fromDate || tooltip.value}
                                dateFormat={DateService.getDateFormat()}
                                placeholderText={DateService.getDateFormat().toLowerCase()}
                                onChangeRaw={e => {
                                    this.setDateRaw('toDate', e.target.value)
                                }}
                                fixedHeight
                                peekNextMonth
                                showYearDropdown
                                showMonthDropdown
                            />
                        </div>
                        <div className="flex-1 ml-5">
                            <MaskedInput
                                className={errors.toDate ? 'error' : ''}
                                type="text"
                                value={form.toTime}
                                ref="toTime"
                                onChange={this.onChangeToTime}
                                mask="11:11:11"
                            />
                        </div>
                    </div>
                ) : (
                    <div>
                        <FormGroup
                            type="text"
                            value={form.toNumeric}
                            validationState={errors.toDate ? 'error' : 'success'}
                            onChange={e =>
                                this.setFormValue('toNumeric', e.target.value, () =>
                                    this.validate(
                                        this.props.mode === 'temporal',
                                        this.getNormalizedInputData(),
                                        this.state.form
                                    )
                                )
                            }
                        />
                    </div>
                )}
                <div>
                    {errors.toDate || errors.fromDate ? (
                        <HelpBlock bsClass="text-danger">
                            {form.errors.get('begin-greater-end') ? (
                                <div>
                                    {mode === 'temporal'
                                        ? i18n('The end date must be after the start date')
                                        : i18n('The final value must be greater than the initial value')}
                                </div>
                            ) : null}
                            {form.errors.get('invalid-from') ? <div>{i18n('Invalid from date')}</div> : null}
                            {form.errors.get('invalid-end') ? <div>{i18n('Invalid end date')}</div> : null}
                        </HelpBlock>
                    ) : null}
                </div>

                <div className="timeline__form__message">
                    <label htmlFor="timeline-to-date-picker">{i18n('Note text')}:</label>
                    <textarea
                        className="form-control"
                        value={form.message}
                        onChange={e => {
                            this.setFormValue('message', e.target.value)
                        }}
                    />

                    {submitted && form.errors.get('message-empty') ? (
                        <div>
                            <HelpBlock bsClass="text-danger">{i18n('This is a required field.')}</HelpBlock>
                        </div>
                    ) : null}
                </div>
                {showActions === false ? null : (
                    <div className="timeline__form-actions">
                        <SaveButton
                            className="btn-lg btn-bold mr-5"
                            disabled={!form.message.length || (submitted && !!form.errors.size)}
                            message={i18n('Note saved successfully')}
                            onClick={this.submit}
                        />
                        <span>{i18n('or')} </span>

                        <a onClick={this.props.onHide} className="cancel">
                            {' '}
                            {i18n('cancel')}
                        </a>
                    </div>
                )}
            </div>
        )
    }
}

/**
 * Gets a unix timestamp from a moment that represents a date, and a timestring.
 *
 * @param {any} mom A moment date.
 * @param {string} [time=''] Time string on the "HH:mm:ss" format
 *
 * @returns {number|NaN} The unix timestamp
 */

export function getUnixFromDateAndTime(mom: moment, time?: string): number {
    time = time || ''
    time = time.replace(/_/g, '0')
    const m = moment(`${mom.format('YYYYMMDD')}${time}`, 'YYYYMMDDHH:mm:ss', true)

    return m.valueOf()
}

function getFormInitialState() {
    return {
        errors: new Map(),
        color: 0,
        message: '',
        toTime: '',
        toNumeric: ''
    }
}
